import React, { useState, useEffect } from 'react';
import { COLORS, TYPOGRAPHY } from '../../lib/styleGuide';

const ACCENT_GRAY = '#757575';
const ERROR_RED = '#F44336';

export default function ShopNameStep({ data, onNext, onBack, isFirst, isLast }) {
  const [shopName, setShopName] = useState(data.shopName || '');
  const [status, setStatus] = useState('idle'); // idle | checking | available | taken | error
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  // Debounced uniqueness check
  useEffect(() => {
    if (!shopName) {
      setStatus('idle');
      setError('');
      return;
    }
    setStatus('checking');
    setError('');
    const timeout = setTimeout(async () => {
      try {
        // Mocked uniqueness check
        const takenNames = ["abby's", "maria's marvelous cakes", "test shop"];
        const normalized = shopName.trim().toLowerCase();
        await new Promise(resolve => setTimeout(resolve, 400));
        if (takenNames.includes(normalized)) {
          setStatus('taken');
          setError('This shop name is already taken.');
        } else {
          setStatus('available');
          setError('');
        }
      } catch (e) {
        setStatus('error');
        setError('Could not validate name. Try again.');
      }
    }, 600);
    return () => clearTimeout(timeout);
  }, [shopName]);

  const handleNext = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!shopName) {
      setError('Shop name is required.');
      return;
    }
    if (status !== 'available') return;
    onNext({ shopName });
  };

  return (
    <form onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div>
        <h2 style={{ fontWeight: 700, fontSize: TYPOGRAPHY.ONBOARDING.H2, margin: 0, color: '#222' }}>Choose Your Shop Name</h2>
        <p style={{ color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: '10px 0 0 0' }}>
          This is how customers will find you. Make it unique and memorable!
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <label htmlFor="shopName" style={{ fontWeight: 600, color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION }}>Shop Name</label>
        <input
          id="shopName"
          name="shopName"
          value={shopName}
          onChange={e => { setShopName(e.target.value); setTouched(true); }}
          style={{
            width: '100%',
            padding: 14,
            borderRadius: 10,
            border: `2px solid ${status === 'taken' || error ? ERROR_RED : status === 'available' ? COLORS.PRIMARY_GRADIENT_START : '#eee'}`,
            fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING,
            fontWeight: 500,
            outline: 'none',
            background: '#FAF9F7',
            color: '#222',
            transition: 'border 0.2s',
          }}
          autoFocus
          aria-invalid={!!error}
          aria-describedby="shopName-status"
        />
        <div id="shopName-status" style={{ minHeight: 22, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, marginTop: 2 }}>
          {status === 'checking' && <span style={{ color: ACCENT_GRAY }}>Checking availability…</span>}
          {status === 'available' && shopName && <span style={{ 
            background: COLORS.PRIMARY_GRADIENT,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>✔️ Name is available!</span>}
          {status === 'taken' && <span style={{ color: ERROR_RED }}>{error}</span>}
          {status === 'error' && <span style={{ color: ERROR_RED }}>{error}</span>}
          {touched && !shopName && <span style={{ color: ERROR_RED }}>Shop name is required.</span>}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        {!isFirst && (
          <button
            type="button"
            onClick={onBack}
            style={{
              background: '#fff',
              color: '#9CA3AF',
              border: 'none',
              borderRadius: 10,
              padding: '14px 32px',
              fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: 44,
              minWidth: 120,
              transition: 'all 0.2s',
            }}
            aria-label="Back"
          >
            Back
          </button>
        )}
        <button
          type="submit"
          disabled={!shopName || status !== 'available'}
          style={{
            background: status === 'available' ? COLORS.PRIMARY_GRADIENT : '#E0CFC2',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '14px 32px',
            fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
              fontWeight: 600,
            cursor: !shopName || status !== 'available' ? 'not-allowed' : 'pointer',
            minHeight: 44,
            minWidth: 120,
            boxShadow: '0 1px 4px #0001',
            transition: 'all 0.2s',
          }}
          aria-label="Next"
        >
          Next
        </button>
      </div>
    </form>
  );
} 